import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import "../../css/SelfService.css";
import profileicon from "../../../assets/signup/profile.svg";
import mail from "../../../assets/signup/mail.svg";
import menu from "../../../assets/signup/menu.svg";
import logout from "../../../assets/signup/logout.svg";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { RiDeleteBin2Line } from "react-icons/ri";
import Header from "../Header/Header";
import { Helmet } from "react-helmet-async";
import {
  getAllTickets,
  getTicketById,
  createSelfServiceTicket,
  updateTicket,
  logoutUser,
} from "../services/api.js";
import axiosInstance from "../../../lib/axios.js";


const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

// ============================================================================
// ENTERPRISE STATUS BADGE CONFIGURATION
// Centralized status configuration similar to Jira, GitHub, Azure DevOps
// ============================================================================

const STATUS_CONFIG = {
  // New/Open statuses
  New: {
    label: "New",
    bgColor: "#E8F1FF",
    textColor: "#1D4ED8",
    borderColor: "#BFDBFE",
  },
  Open: {
    label: "Open",
    bgColor: "#DBEAFE",
    textColor: "#2563EB",
    borderColor: "#93C5FD",
  },
  Open_New: {
    label: "New",
    bgColor: "#E8F1FF",
    textColor: "#1D4ED8",
    borderColor: "#BFDBFE",
  },
  Open_Assigned: {
    label: "Assigned",
    bgColor: "#DBEAFE",
    textColor: "#2563EB",
    borderColor: "#93C5FD",
  },
  ReOpen: {
    label: "Reopened",
    bgColor: "#FEF3C7",
    textColor: "#B45309",
    borderColor: "#FCD34D",
  },

  // In Progress statuses
  "In Progress": {
    label: "In Progress",
    bgColor: "#FEF3C7",
    textColor: "#B45309",
    borderColor: "#FCD34D",
  },

  // Pending statuses
  Pending: {
    label: "Pending",
    bgColor: "#FFF7ED",
    textColor: "#C2410C",
    borderColor: "#FED7AA",
  },
  Open_Pending: {
    label: "Pending",
    bgColor: "#FFF7ED",
    textColor: "#C2410C",
    borderColor: "#FED7AA",
  },
  "Pending Auto Close": {
    label: "Pending Auto Close",
    bgColor: "#FCE7F3",
    textColor: "#BE185D",
    borderColor: "#F9A8D4",
  },
  "Pending Reminder": {
    label: "Pending Reminder",
    bgColor: "#FCE7F3",
    textColor: "#BE185D",
    borderColor: "#F9A8D4",
  },
  "Pending Risk": {
    label: "Pending Risk",
    bgColor: "#EDE9FE",
    textColor: "#6D28D9",
    borderColor: "#C4B5FD",
  },

  // Approval statuses
  "Waiting for Approval": {
    label: "Waiting for Approval",
    bgColor: "#F3E8FF",
    textColor: "#7E22CE",
    borderColor: "#D8B4FE",
  },
  Approved: {
    label: "Approved",
    bgColor: "#D1FAE5",
    textColor: "#047857",
    borderColor: "#6EE7B7",
  },
  Rejected: {
    label: "Rejected",
    bgColor: "#FEE2E2",
    textColor: "#DC2626",
    borderColor: "#FCA5A5",
  },
  "Adjustment Required": {
    label: "Adjustment Required",
    bgColor: "#FEE2E2",
    textColor: "#DC2626",
    borderColor: "#FCA5A5",
  },

  // Resolution statuses
  Resolved: {
    label: "Resolved",
    bgColor: "#D1FAE5",
    textColor: "#065F46",
    borderColor: "#6EE7B7",
  },
  Fixed: {
    label: "Fixed",
    bgColor: "#D1FAE5",
    textColor: "#065F46",
    borderColor: "#6EE7B7",
  },

  // Closed statuses
  Closed: {
    label: "Closed",
    bgColor: "#E5E7EB",
    textColor: "#4B5563",
    borderColor: "#D1D5DB",
  },
  Closed_Closed: {
    label: "Closed",
    bgColor: "#E5E7EB",
    textColor: "#4B5563",
    borderColor: "#D1D5DB",
  },
  Cancelled: {
    label: "Cancelled",
    bgColor: "#FEE2E2",
    textColor: "#B91C1C",
    borderColor: "#FCA5A5",
  },
  Removed: {
    label: "Removed",
    bgColor: "#F3F4F6",
    textColor: "#6B7280",
    borderColor: "#D1D5DB",
  },

  // Merge/Branch statuses
  Merged: {
    label: "Merged",
    bgColor: "#EDE9FE",
    textColor: "#7C3AED",
    borderColor: "#C4B5FD",
  },

  // Risk statuses
  "Risk Accepted": {
    label: "Risk Accepted",
    bgColor: "#EDE9FE",
    textColor: "#6D28D9",
    borderColor: "#C4B5FD",
  },

  // Scan statuses
  "Rescan Requested": {
    label: "Rescan Requested",
    bgColor: "#E0F2FE",
    textColor: "#0369A1",
    borderColor: "#7DD3FC",
  },
  "Rescan In Progress": {
    label: "Rescan In Progress",
    bgColor: "#E0F2FE",
    textColor: "#0369A1",
    borderColor: "#7DD3FC",
  },
  "Scan Completed": {
    label: "Scan Completed",
    bgColor: "#D1FAE5",
    textColor: "#047857",
    borderColor: "#6EE7B7",
  },
};

/**
 * Get status configuration for a given status
 * @param {string} status - The status value
 * @returns {Object} Status configuration with label, bgColor, textColor, borderColor
 */
const getStatusConfig = (status) => {
  // Normalize status input
  const normalizedStatus = status?.trim() || "";

  // Return matching config or default
  return (
    STATUS_CONFIG[normalizedStatus] || {
      label: normalizedStatus || "Unknown",
      bgColor: "#F3F4F6",
      textColor: "#6B7280",
      borderColor: "#E5E7EB",
    }
  );
};

/**
 * Enterprise Status Badge Component
 * Reusable status badge with professional styling
 */
const StatusBadge = ({ status, className = "" }) => {
  const config = getStatusConfig(status);

  const badgeStyle = {
    backgroundColor: config.bgColor,
    color: config.textColor,
    borderColor: config.borderColor,
  };

  return (
    <span
      className={`fd-status-badge ${className}`}
      style={badgeStyle}
      title={config.label}
    >
      {config.label}
    </span>
  );
};

export default function SelfserviceTable() {
  const location = useLocation();
  const title = "Ensure quality with the best API testing tools today";
  const metaDescription =
    "Ensure API reliability, performance, and security with the best api testing tools, making testing faster, smarter, and more accurate for modern software.";

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [self, setSelf] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [ticketType, setTicketType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subject, setSubject] = useState("");
  const [commentDescription, setCommentDescription] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [status, setStatus] = useState("idle");
  const [opencount, setOpenCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingTicketDetails, setLoadingTicketDetails] = useState(false);
  const [isLoadingTickets, setIsLoadingTickets] = useState(true);
  const fileInputRef = useRef(null);
  const commentsRef = useRef(null);
  const [user, setUser] = useState(null);
  const hasLoadedTickets = useRef(false);

  // Create ticket form state
  const [ticketFormData, setTicketFormData] = useState({
    title: "",
    description: "",
    severity: "",
    attachments: [],
  });

  const [ticketErrors, setTicketErrors] = useState({});
  const [ticketTouched, setTicketTouched] = useState({});

  const loadTickets = async () => {
    try {
      setIsLoadingTickets(true);
      console.log("Loading tickets...");
      const response = await getAllTickets();

      console.log("Tickets response:", response);

      if (response.success) {
        // Handle new response format with count field
        const tickets = response.data?.tickets || response.data || [];
        setSelf(tickets);

        setOpenCount(
          tickets.filter((ticket) => {
            const status = ticket.status?.toLowerCase();

            return status !== "closed" && status !== "resolved";
          }).length,
        );
      } else {
        toast.error(response.message || "Failed to load tickets");
      }
    } catch (error) {
      console.error("Error loading tickets:", error);
      toast.error(error.error || error.message || "Failed to load tickets");
    } finally {
      setIsLoadingTickets(false);
    }
  };


useEffect(() => {
  // Read token and username from URL
  const params = new URLSearchParams(window.location.search);

  const token = params.get("token");
  const username = params.get("username");

  // If coming from TestOps, save them
  if (token && username) {
    localStorage.setItem("ssToken", token);
    localStorage.setItem("sessionId", token);
    localStorage.setItem(
      "ssUser",
      JSON.stringify({
        username,
        email: username,
        name: username,
      })
    );

    // Optional: Remove token from URL
    window.history.replaceState({}, "", "/self-servicedashboard");
  }

  // Prevent loading twice
  if (hasLoadedTickets.current) {
    return;
  }

  const storedUser = localStorage.getItem("ssUser");

  if (storedUser) {
    const userData = JSON.parse(storedUser);
    setUser(userData);

    loadTickets();

    hasLoadedTickets.current = true;
  } else {
    window.location.replace("/login");
  }
}, []);


  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  // Severity options
  const severityOptions = ["Critical", "High", "Medium", "Low"];

  // Map severity to priority levels
  const mapSeverityToPriority = (severity) => {
    const severityMap = {
      Critical: "S1",
      High: "S2",
      Medium: "S3",
      Low: "S4",
    };
    return severityMap[severity] || "S3";
  };

  // Validate field
  const validateTicketField = (name, value) => {
    switch (name) {
      case "title":
        if (!value.trim()) return "Title is required";
        if (value.trim().length < 5)
          return "Title must be at least 5 characters";
        return "";
      case "description":
        if (!value.trim()) return "Description is required";
        if (value.trim().length < 10)
          return "Description must be at least 10 characters";
        return "";
      case "severity":
        if (!value) return "Please select severity";
        return "";
      default:
        return "";
    }
  };

  // Handle ticket form input change
  const handleTicketInputChange = (e) => {
    const { name, value } = e.target;
    setTicketFormData({
      ...ticketFormData,
      [name]: value,
    });

    // Real-time validation for touched fields
    if (ticketTouched[name]) {
      const error = validateTicketField(name, value);
      setTicketErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  // Handle ticket form input blur
  const handleTicketInputBlur = (e) => {
    const { name } = e.target;
    setTicketTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateTicketField(name, ticketFormData[name]);
    setTicketErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Handle file selection for ticket creation
  const handleTicketFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Allow only 1 file
    if (files.length > 1) {
      toast.error("Only 1 file allowed. Please select a single file.");
      return;
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      const oversizedNames = oversizedFiles.map((f) => f.name).join(", ");
      toast.error(`File exceeds 5MB limit: ${oversizedNames}`);
      return;
    }

    setTicketFormData({
      ...ticketFormData,
      attachments: files,
    });
  };

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Reset ticket form
  const resetTicketForm = () => {
    setTicketFormData({
      title: "",
      description: "",
      severity: "",
      attachments: [],
    });
    setTicketErrors({});
    setTicketTouched({});
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Validate ticket form
  const validateTicketForm = () => {
    const newErrors = {};
    ["title", "description", "severity"].forEach((key) => {
      const error = validateTicketField(key, ticketFormData[key]);
      if (error) newErrors[key] = error;
    });
    return newErrors;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "--";

    const [date] = dateStr.split(" ");

    const [year, month, day] = date.split("-");

    return `${day}-${month}-${year}`;
  };

  const ResetForm = () => {
    resetTicketForm();
    setSubject("");
    setCommentDescription("");
    setCommentFiles([]);
    setStatus("idle");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleTicketChange = (e) => {
    const selected = e.target.value;

    setTicketType(selected);

    if (selected) {
      setPopupOpen(true);
    }
  };

  // Get unique statuses from ticket data for dynamic filter
  const availableStatuses = useMemo(() => {
    if (!Array.isArray(self) || self.length === 0) return [];

    // Extract unique statuses
    const uniqueStatuses = [
      ...new Set(self.map((ticket) => ticket.status).filter(Boolean)),
    ];

    // Sort by status category and label
    const statusOrder = {
      Open_New: 1,
      Open_Assigned: 2,
      Open_Pending: 3,
      "In Progress": 4,
      Pending: 5,
      "Waiting for Approval": 6,
      Approved: 7,
      Rejected: 8,
      "Adjustment Required": 9,
      ReOpen: 10,
      Resolved: 11,
      Fixed: 12,
      Closed: 13,
      Closed_Closed: 14,
      Cancelled: 15,
      Removed: 16,
      Merged: 17,
      "Risk Accepted": 18,
      "Rescan Requested": 19,
      "Rescan In Progress": 20,
      "Scan Completed": 21,
      "Pending Auto Close": 22,
      "Pending Reminder": 23,
      "Pending Risk": 24,
    };

    return uniqueStatuses.sort((a, b) => {
      const orderA = statusOrder[a] ?? 999;
      const orderB = statusOrder[b] ?? 999;
      return orderA - orderB;
    });
  }, [self]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(self)) return [];

    const filtered = self.filter((row) => {
      const searchTerm = search.trim().toLowerCase();

      const dateOnly = String(row.createdOn ?? "").toLowerCase();

      const matchesSearch =
        String(row.caseNumber ?? "")
          .toLowerCase()
          .includes(searchTerm) ||
        String(row.title ?? "")
          .toLowerCase()
          .includes(searchTerm) ||
        String(row.ticketType ?? "")
          .toLowerCase()
          .includes(searchTerm) ||
        dateOnly.includes(searchTerm) ||
        String(row.assignedTo ?? "")
          .toLowerCase()
          .includes(searchTerm);

      const matchesStatus = statusFilter
        ? String(row.status ?? "").toLowerCase() === statusFilter.toLowerCase()
        : true;

      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      return new Date(b.createdOn || 0) - new Date(a.createdOn || 0);
    });

    return filtered;
  }, [search, self, statusFilter]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const paginatedData = filteredData.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const startIdx = filteredData.length === 0 ? 0 : (page - 1) * pageSize + 1;

  const endIdx =
    filteredData.length === 0
      ? 0
      : Math.min(page * pageSize, filteredData.length);

  const handleCaseNumberClick = async (row) => {
    try {
      setLoadingTicketDetails(true);

      const sessionId = localStorage.getItem("sessionId");

      if (!sessionId) {
        toast.error("No active session. Please log in again.");
        return;
      }

      const response = await getTicketById(row.id, sessionId);

      if (response.success && response.data) {
        // Validate response data before setting
        const ticketData = {
          id: response.data.id || row.id,
          caseNumber: response.data.caseNumber || "-",
          title: response.data.title || "-",
          status: response.data.status || "-",
          severity: response.data.severity || "-",
          created_by: response.data.created_by || "-",
          createdDate: response.data.createdDate || "-",
          assignedTo: response.data.assignedTo || "-",
          resolution_target_time: response.data.resolution_target_time || "-",
          ticketDescription: response.data.ticketDescription || "-",
          resolution: response.data.resolution || "-",
          documents: Array.isArray(response.data.documents)
            ? response.data.documents
            : [],
          notes: Array.isArray(response.data.notes) ? response.data.notes : [],
        };
        setSelectedTicket(ticketData);
        setModalOpen(true);
      } else {
        toast.error(response.message || "Failed to load ticket details");
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      toast.error(
        error.message || error.error || "Error loading ticket details",
      );
    } finally {
      setLoadingTicketDetails(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local session
      localStorage.removeItem("ssUser");
      localStorage.removeItem("ssToken");
      localStorage.removeItem("sessionId");

      toast.success("Logged out");

      setTimeout(() => {
        window.location.replace("/login");
      }, 1000);
    }
  };

  const handleShowComments = () => {
    setShowComments(true);

    setTimeout(() => {
      commentsRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 100);
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = { title: true, description: true, severity: true };
    setTicketTouched(allTouched);

    // Validate form
    const newErrors = validateTicketForm();
    setTicketErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user credentials from localStorage
      const storedUser = localStorage.getItem("ssUser");

      if (!storedUser) {
        throw new Error("User session not found. Please login again.");
      }

      const userData = JSON.parse(storedUser);

      const username = userData.username;
      const customerEmail = userData.email;

      if (!username) {
        throw new Error("Invalid user credentials. Please login again.");
      }

      // Prepare ticket data - CustomerUser is auto-populated from JWT
      const ticketData = {
        Ticket: {
          Type: ticketType,
          Title: ticketFormData.title,
          Queue: "FrothtestopsSSP",
          State: "New",
          Priority: mapSeverityToPriority(ticketFormData.severity),
          // CustomerUser is auto-populated from JWT username - no longer needed in request
        },
        Article: {
          Subject: ticketFormData.title,
          Body: ticketFormData.description,
          ContentType: "text/plain; charset=utf-8",
        },
      };

      // Process attachments if any
      if (ticketFormData.attachments && ticketFormData.attachments.length > 0) {
        const attachmentPromises = ticketFormData.attachments.map(
          async (file) => {
            try {
              // Check file size (5MB limit)
              if (file.size > 5 * 1024 * 1024) {
                toast.error(
                  `File ${file.name} exceeds 5MB limit and will not be attached`,
                );
                return null;
              }

              const base64Content = await fileToBase64(file);
              return {
                Content: base64Content,
                ContentType: file.type || "application/octet-stream",
                Filename: file.name,
              };
            } catch (error) {
              console.error("Error converting file to base64:", error);
              toast.error(`Failed to convert file ${file.name}`);
              return null;
            }
          },
        );

        const processedAttachments = await Promise.all(attachmentPromises);
        ticketData.Attachment = processedAttachments.filter((a) => a !== null);
      }

      console.log("Creating ticket:", ticketData);

      const sessionId = localStorage.getItem("sessionId");

      if (!sessionId) {
        throw new Error("Session expired. Please login again.");
      }

      // Call API with JWT token (no username/password needed)
      const response = await createSelfServiceTicket(
        ticketData,
        sessionId,
      );

      if (response.success) {
        toast.success("Ticket created successfully!");

        setPopupOpen(false);

        resetTicketForm();

        await loadTickets();
      } else {
        toast.error(response.message || "Failed to create ticket");
      }
    } catch (error) {
      console.error("Error submitting Create Ticket:", error);

      toast.error(
        error.error || error.message || "Error submitting Create Ticket!",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTicket) {
      return;
    }

    // Get user credentials from localStorage
    const storedUser = localStorage.getItem("ssUser");

    if (!storedUser) {
      toast.error("User not found. Please log in again.");
      return;
    }

    const userData = JSON.parse(storedUser);
    const username = userData.username;

    if (!username) {
      toast.error("Invalid user credentials. Please log in again.");
      return;
    }

    try {
      setStatus("loading");

      // Process attachments if any
      let attachments = [];
      if (commentFiles && commentFiles.length > 0) {
        const attachmentPromises = commentFiles.map(async (file) => {
          try {
            const base64Content = await fileToBase64(file);
            return {
              Content: base64Content,
              ContentType: file.type || "application/octet-stream",
              Filename: file.name,
            };
          } catch (error) {
            console.error("Error converting file to base64:", error);
            return null;
          }
        });

        const processedAttachments = await Promise.all(attachmentPromises);
        attachments = processedAttachments.filter((a) => a !== null);
      }

      // Prepare update data - new backend format
      const updateData = {
        state: selectedTicket.state || selectedTicket.status || "Open",
        comment: commentDescription,
        Article: {
          Subject: subject,
          Body: commentDescription,
          ContentType: "text/plain; charset=utf-8",
        },
        Attachment: attachments,
      };

      console.log("Submitting comment:", updateData);

      const sessionId = localStorage.getItem("sessionId");
      // New backend format - JWT token via query parameter, no username/password needed
      const response = await updateTicket(
        selectedTicket.id,
        updateData,
        sessionId,
      );

      if (response.success) {
        toast.success("Comment added successfully!");

        setStatus("success");

        setSubject("");
        setCommentDescription("");
        setCommentFiles([]);

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        await loadTickets();

        setTimeout(() => {
          setStatus("idle");
        }, 2000);
      } else {
        toast.error(response.message || "Failed to add comment");
        setStatus("idle");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error(error.message || error.error || "Error adding comment!");
      setStatus("idle");
    }
  };

  const downloadAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.Content);

      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: attachment.ContentType,
      });

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = attachment.Filename || "attachment";

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error("Failed to download attachment");
    }
  };
  // Handle authenticated file download
  const handleFileDownload = async (downloadUrl, filename) => {
    if (!downloadUrl || downloadUrl === "#") {
      console.error("Invalid download URL:", downloadUrl);
      toast.error("Invalid download URL");
      return;
    }

    try {
      toast.loading("Downloading file...", { id: "download" });

      console.log("Downloading from:", downloadUrl);

      // Use axios instance to fetch with authentication
      const response = await axiosInstance.get(downloadUrl, {
        responseType: "blob",
      });

      console.log("Download response:", response);

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("File downloaded successfully", { id: "download" });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to download file",
        { id: "download" },
      );
    }
  };

  return (
    <>
      <Helmet>
        <title>Ensure quality with the best API testing tools today</title>

        <meta name="ticketDescription" content={metaDescription} />

        <meta name="keywords" content="best api testing tools" />

        <link rel="canonical" href="https://frothtestops.com/self-service" />

        {/* Open Graph Tags */}
        <meta property="og:title" content={title} />
        <meta property="og:ticketDescription" content={metaDescription} />
        <meta property="og:image" content="/images/og-image.jpg" />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://frothtestops.com${location.pathname}`}
        />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:ticketDescription" content={metaDescription} />
        <meta name="twitter:image" content="/images/og-image.jpg" />
      </Helmet>
      <Header />

      <Toaster position="top-right" />

      <div
        className={`fd-main-wrapper ${location.pathname !== "/" ? "page-padding" : ""}`}
      >
        <div className="fd-container">
          {/* TOP HEADER */}

          <div className="fd-topbar">
            <nav className="fd-breadcrumb">
              <a href="/">Home</a>

              {" / "}

              <span>Self Service</span>
            </nav>

            <div
              className="position-relative"
              onMouseEnter={() => setIsOpen(true)}
              onMouseLeave={() => setIsOpen(false)}
            >
              <button className="fd-profile-btn">
                <img src={profileicon} alt="profile" />
                <span className="fd-profile-name">{user?.name}</span>
              </button>

              {isOpen && (
                <div className="fd-dropdown-menu">
                  <div
                    className="fd-dropdown-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="fd-dropdown-flex">
                      <img src={profileicon} alt="" />

                      <span className="fd-dropdown-text">{user?.name}</span> 

                    </div>
                  </div>

                  <div
                    className="fd-dropdown-item"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="fd-dropdown-flex">
                      <img src={mail} alt="" />

                      <span className="fd-dropdown-text">{user?.email}</span>
                    </div>
                  </div>

                  {/* <div className="fd-dropdown-item">
                    <a href="/my-account" className="text-decoration-none">
                      <div className="fd-dropdown-flex">
                        <img  src={menu}  alt="" />

                        <span className="fd-dropdown-text">My Account</span>
                      </div>
                    </a>
                  </div> */}

                  <div className="fd-dropdown-item" onClick={handleLogout}>
                    <div className="fd-dropdown-flex">
                      <img src={logout} alt="" />

                      <span className="fd-dropdown-text">Logout</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* FILTER SECTION */}

          <div className="fd-filter-box">
            <div className="fd-filter-top">
              <div className="fd-ticket-stats">
                <p>
                  Total Ticket :<span> {self.length}</span>
                </p>

                <p>
                  Open Ticket :<span> {opencount}</span>
                </p>
              </div>

              <div className="fd-filter-actions">
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);

                    setPage(1);
                  }}
                  className="fd-input"
                />

                <select
                  value={ticketType}
                  onChange={handleTicketChange}
                  className="fd-select"
                >
                  <option value="">Create New Ticket</option>

                  <option value="Incident">Incident</option>

                  <option value="Service Request">Service Request</option>

                  <option value="Change Request">Change Request</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="fd-select fd-status-select"
                  disabled={availableStatuses.length === 0}
                >
                  <option value="">All</option>

                  {availableStatuses.map((status) => {
                    const config = getStatusConfig(status);
                    return (
                      <option key={status} value={status}>
                        {config.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            {/* TABLE */}

            <div className="fd-table-wrapper">
              <table className="fd-table">
                <thead>
                  <tr>
                    <th>S. No</th>

                    <th>Case Number</th>

                    <th>Case Title</th>

                    <th>Ticket Type</th>

                    <th>Raised On</th>

                    <th>Assigned To</th>

                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {isLoadingTickets ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <div
                            className="spinner-border spinner-border-sm mb-2"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span className="text-muted">Loading tickets...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={7}>No data found.</td>
                    </tr>
                  ) : (
                    paginatedData.map((row, idx) => (
                      <tr key={row.id || idx}>
                        <td>
                          {String((page - 1) * pageSize + idx + 1).padStart(
                            2,
                            "0",
                          )}
                        </td>

                        <td
                          className="fd-case-link"
                          onClick={() => handleCaseNumberClick(row)}
                        >
                          {row.caseNumber}
                        </td>

                        <td>{row.title}</td>

                        <td>{row.ticketType}</td>

                        <td>{formatDate(row.createdOn)}</td>

                        <td>{row.assignedTo || "-"}</td>

                        <td>
                          <StatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}

            <div className="fd-pagination">
              <div className="fd-pagination-left">
                <span>Items per page:</span>

                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="fd-select fd-status-select fd-select-page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fd-pagination-right">
                <span>
                  {startIdx}-{endIdx} of {filteredData.length}
                </span>

                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="fd-page-btn"
                >
                  ◀
                </button>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || totalPages === 0}
                  className="fd-page-btn"
                >
                  ▶
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE TICKET MODAL */}

      {popupOpen && (
        <div className="fd-modal-overlay d-flex justify-content-center align-items-center">
          <div className="fd-modal">
            <form onSubmit={handleTicketSubmit}>
              <div className="fd-modal-title">Create {ticketType}</div>

              {/* Title */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Title <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  className={`form-control ${
                    ticketTouched.title && ticketErrors.title
                      ? "is-invalid"
                      : ""
                  } ${
                    ticketTouched.title &&
                    !ticketErrors.title &&
                    ticketFormData.title
                      ? "is-valid"
                      : ""
                  }`}
                  name="title"
                  value={ticketFormData.title}
                  onChange={handleTicketInputChange}
                  onBlur={handleTicketInputBlur}
                  placeholder="Enter ticket title"
                />
                {ticketTouched.title && ticketErrors.title && (
                  <div className="invalid-feedback">{ticketErrors.title}</div>
                )}
              </div>

              {/* Description */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Description <span className="text-danger">*</span>
                </label>

                <textarea
                  rows={2}
                  className={`form-control ${
                    ticketTouched.description && ticketErrors.description
                      ? "is-invalid"
                      : ""
                  } ${
                    ticketTouched.description &&
                    !ticketErrors.description &&
                    ticketFormData.description
                      ? "is-valid"
                      : ""
                  }`}
                  name="description"
                  value={ticketFormData.description}
                  onChange={handleTicketInputChange}
                  onBlur={handleTicketInputBlur}
                  placeholder="Provide detailed description"
                />
                {ticketTouched.description && ticketErrors.description && (
                  <div className="invalid-feedback">
                    {ticketErrors.description}
                  </div>
                )}
              </div>

              {/* Severity */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Severity <span className="text-danger">*</span>
                </label>

                <select
                  className={`form-select ${
                    ticketTouched.severity && ticketErrors.severity
                      ? "is-invalid"
                      : ""
                  } ${
                    ticketTouched.severity &&
                    !ticketErrors.severity &&
                    ticketFormData.severity
                      ? "is-valid"
                      : ""
                  }`}
                  name="severity"
                  value={ticketFormData.severity}
                  onChange={handleTicketInputChange}
                  onBlur={handleTicketInputBlur}
                >
                  <option value="">Choose...</option>
                  {severityOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                {ticketTouched.severity && ticketErrors.severity && (
                  <div className="invalid-feedback">
                    {ticketErrors.severity}
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Attachment (Optional)
                </label>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleTicketFileChange}
                  className="form-control"
                />
                <div className="form-text">
                  Max 1 file, 5MB limit. Supported: PNG, JPG, PDF, DOC, DOCX,
                  XLS, XLSX, TXT
                </div>
              </div>

              {ticketFormData.attachments.length > 0 && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    Selected Files:
                  </label>
                  {ticketFormData.attachments.map((file, index) => (
                    <div key={index} className="fd-file-item">
                      <span>{file.name}</span>

                      <span className="text-muted ms-2">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>

                      <button
                        type="button"
                        className="btn btn-sm btn-danger ms-auto"
                        onClick={() => {
                          setTicketFormData({
                            ...ticketFormData,
                            attachments: ticketFormData.attachments.filter(
                              (_, i) => i !== index,
                            ),
                          });
                        }}
                      >
                        <RiDeleteBin2Line />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    resetTicketForm();
                    setPopupOpen(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VIEW TICKET MODAL */}

      <AnimatePresence>
        {modalOpen && selectedTicket && (
          <>
            <motion.div
              className="fd-modal-overlay"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            />

            <motion.div
              className="position-fixed top-50 start-50 translate-middle w-100 d-flex justify-content-center"
              initial={{
                scale: 0.8,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0.8,
                opacity: 0,
              }}
              transition={{
                duration: 0.2,
              }}
              style={{
                zIndex: 1060,
              }}
            >
              <div className="ticket-modal">
                <button
                  className="ticket-close-btn"
                  onClick={() => {
                    setModalOpen(false);
                    setShowComments(false);
                  }}
                >
                  ×
                </button>
                <div className="ticket-box">
                  <div className="ticket-box-header">
                    <h2>Ticket Details</h2>
                  </div>

                  {loadingTicketDetails ? (
                    <div className="ticket-loading">
                      Loading ticket details...
                    </div>
                  ) : (
                    <div className="ticket-box-content">
                      <div>
                        <strong>Case Number:</strong>{" "}
                        {selectedTicket.caseNumber}
                      </div>

                      <div>
                        <strong>Status:</strong>{" "}
                        <StatusBadge status={selectedTicket.status} />
                      </div>

                      <div>
                        <strong>Case Title:</strong>{" "}
                        {selectedTicket.title || "-"}
                      </div>

                      <div>
                        <strong>Severity:</strong>{" "}
                        {selectedTicket.severity || "-"}
                      </div>

                      <div>
                        <strong>Created on:</strong>{" "}
                        {selectedTicket.createdDate || "-"}
                      </div>

                      <div>
                        <strong>SLA Expires on:</strong>{" "}
                        {selectedTicket.resolution_target_time || "-"}
                      </div>

                      <div className="ticket-col-span">
                        <strong>Assign To:</strong>{" "}
                        {selectedTicket.assignedTo || "-"}
                      </div>

                      <div className="ticket-col-span">
                        <strong>Created By:</strong>{" "}
                        {selectedTicket.created_by || "-"}
                      </div>

                      

                      <div className="ticket-col-span">
                        <strong>Description:</strong>{" "}
                        {selectedTicket.ticketDescription || "-"}
                      </div>

                      <div className="ticket-col-span">
                        <strong>Attachments:</strong>

                        {selectedTicket.documents?.length > 0 ? (
                          <div className="ticket-attachments">
                            <ol>
                              {selectedTicket.documents.map((doc, idx) => {
                                // Debug: log the document structure
                                console.log("Document", idx, ":", doc);

                                const downloadUrl =
                                  doc.download_url ||
                                  doc.DownloadURL ||
                                  doc.FileURL ||
                                  doc.file_url ||
                                  doc.url ||
                                  doc.ContentURL ||
                                  doc.content_url ||
                                  doc.attachment_url ||
                                  doc.AttachmentURL ||
                                  doc.filepath ||
                                  doc.Filepath ||
                                  doc.path;

                                const filename =
                                  doc.Filename ||
                                  doc.filename ||
                                  doc.document_name ||
                                  doc.DocumentName ||
                                  doc.name ||
                                  doc.Name ||
                                  doc.Title ||
                                  doc.title ||
                                  "Unnamed file";

                                return (
                                  <li key={idx}>
                                    <button
                                      onClick={() => downloadAttachment(doc)}
                                      className="ticket-doc-link"
                                      style={{
                                        background: "none",
                                        border: "none",
                                        color: "#007bff",
                                        cursor: "pointer",
                                        padding: 0,
                                        textAlign: "left",
                                      }}
                                    >
                                      {filename}
                                    </button>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        ) : (
                          <span> - </span>
                        )}
                      </div>
                    </div>
                  )}

                  {!showComments && (
                    <div className="ticket-comment-toggle">
                      <button
                        className="ticket-comment-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowComments();
                        }}
                      >
                        <IoIosArrowDown />

                        <span>Comments</span>
                      </button>
                    </div>
                  )}
                  {showComments && !loadingTicketDetails && (
                    <div className="ticket-comments-box" ref={commentsRef}>
                      {/* COMMENTS TABLE */}

                      <div className="ticket-comments-section">
                        <div className="ticket-comments-title">Comments:</div>

                        <div className="ticket-comments-table-wrapper">
                          <table className="ticket-comments-table">
                            <thead>
                              <tr>
                                <th>S. No</th>

                                <th>Date</th>

                                <th>Subject</th>

                                <th>Comments</th>

                                <th>Attachments</th>
                              </tr>
                            </thead>

                            <tbody>
                              {selectedTicket.notes?.length > 0 ? (
                                selectedTicket.notes.map((note, index) => (
                                  <tr key={note.id || index}>
                                    <td>{index + 1}</td>

                                    <td>
                                      {new Date(
                                        note.date_entered,
                                      ).toLocaleDateString("en-GB")}
                                    </td>

                                    <td>{note.subject}</td>

                                    <td>{note.Body}</td>

                                    <td>
                                      {note.Attachment &&
                                      note.Attachment.length > 0 ? (
                                        note.Attachment.map((att, attIdx) => (
                                          <div key={attIdx}>
                                            <button
                                              className="ticket-doc-link"
                                              onClick={() =>
                                                downloadAttachment(att)
                                              }
                                              style={{
                                                background: "none",
                                                border: "none",
                                                color: "#007bff",
                                                cursor: "pointer",
                                                padding: 0,
                                                textAlign: "left",
                                              }}
                                            >
                                              {att.Filename ||
                                                att.filename ||
                                                att.DocumentName ||
                                                "-"}
                                            </button>
                                          </div>
                                        ))
                                      ) : note.attachment ? (
                                        <button
                                          onClick={() =>
                                            handleFileDownload(
                                              note.attachment.download_url ||
                                                note.attachment.DownloadURL ||
                                                note.attachment.FileURL,
                                              note.attachment.Filename ||
                                                note.attachment.filename ||
                                                note.attachment.DocumentName ||
                                                "attachment",
                                            )
                                          }
                                          className="ticket-doc-link"
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#007bff",
                                            cursor: "pointer",
                                            padding: 0,
                                            textAlign: "left",
                                          }}
                                        >
                                          {note.attachment.Filename ||
                                            note.attachment.filename ||
                                            note.attachment.DocumentName ||
                                            "Unnamed file"}
                                        </button>
                                      ) : (
                                        <span className="ticket-no-attachment">
                                          -
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={5} className="ticket-empty-row">
                                    No comments available.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* COMMENT FORM */}

                      {selectedTicket?.state !== "Closed" && (
                        <form onSubmit={handleNoteSubmit}>
                          <div>
                            {/* SUBJECT */}

                            <div className="ticket-label">
                              Subject
                              <span className="ticket-required">*</span>
                            </div>

                            <input
                              type="text"
                              placeholder="Title"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              className="ticket-input"
                              required
                            />

                            {/* COMMENTS */}

                            <div className="ticket-label">
                              Comments
                              <span className="ticket-required">*</span>
                            </div>

                            <textarea
                              placeholder="Description"
                              className="ticket-textarea"
                              rows={3}
                              value={commentDescription}
                              onChange={(e) =>
                                setCommentDescription(e.target.value)
                              }
                              required
                            />
                          </div>

                          {/* FILE UPLOAD */}

                          <div className="ticket-upload-section">
                            <label className="ticket-label">Add File</label>

                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={(e) => {
                                const selected = Array.from(
                                  e.target.files || [],
                                );

                                // Allow only 1 file
                                if (selected.length > 1) {
                                  toast.error(
                                    "Only 1 file allowed. Please select a single file.",
                                  );
                                  return;
                                }

                                // Check file size (5MB limit)
                                const maxSize = 5 * 1024 * 1024;
                                const oversizedFiles = selected.filter(
                                  (file) => file.size > maxSize,
                                );

                                if (oversizedFiles.length > 0) {
                                  const oversizedNames = oversizedFiles
                                    .map((f) => f.name)
                                    .join(", ");
                                  toast.error(
                                    `File exceeds 5MB limit: ${oversizedNames}`,
                                  );
                                  return;
                                }

                                setCommentFiles(selected);
                              }}
                              className="ticket-upload-input"
                            />

                            {commentFiles.length > 0 && (
                              <div className="ticket-file-list">
                                {commentFiles.map((file, index) => (
                                  <div key={index} className="ticket-file-item">
                                    <span className="ticket-file-name">
                                      {file.name}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCommentFiles(
                                          commentFiles.filter(
                                            (_, i) => i !== index,
                                          ),
                                        );
                                      }}
                                      className="ticket-file-remove"
                                    >
                                      <RiDeleteBin2Line size={18} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* BOTTOM ACTIONS */}

                          <div className="ticket-bottom-actions">
                            <div className="ticket-hide-wrapper">
                              <button
                                type="button"
                                className="ticket-comment-btn"
                                onClick={() => setShowComments(false)}
                              >
                                <IoIosArrowUp />

                                <span>Hide Comments</span>
                              </button>
                            </div>

                            <div className="ticket-right-actions">
                              <button
                                type="button"
                                onClick={() => {
                                  ResetForm();

                                  setModalOpen(false);

                                  setShowComments(false);
                                }}
                                className="ticket-btn-red"
                              >
                                Close
                              </button>

                              <button
                                type="submit"
                                disabled={
                                  status === "loading" || status === "success"
                                }
                                className={`ticket-btn-green
              ${
                status === "loading" || status === "success"
                  ? "ticket-btn-disabled"
                  : ""
              }`}
                              >
                                {status === "loading"
                                  ? "Sending..."
                                  : status === "success"
                                    ? "Sent ✅"
                                    : "Send"}
                              </button>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
